import { DEFAULT_PROXY, IndexedDBCache } from '@skinsoft/data-store';
import { fetch } from '@skinsoft/network';
import { Observable, of } from 'rxjs';
import { catchError, first, map, retry, switchMap, tap } from 'rxjs/operators';

export class MediaTransferHelper {

    public static getInstance() {
        if (!MediaTransferHelper.instance) {
            MediaTransferHelper.instance = new MediaTransferHelper();
        }

        return MediaTransferHelper.instance;
    }

    public static install(Vue) {
        Vue.prototype.$mediaTransferHelper = MediaTransferHelper.getInstance();
    }

    private static instance: MediaTransferHelper = null as any;

    private proxy = DEFAULT_PROXY;
    private cache = new IndexedDBCache();

    public downloadOriginalPDFFile(mediaId: string): Observable<any> {
        return this.proxy.fetch("/downloadMedias/originalFile",
            {
                "mediaId": mediaId,
            },
            'GET', undefined, undefined, true, true)
            .pipe(
                catchError((error) => of(undefined)),
                first())
    }

    public downloadMedia(mediaId: string, fileType = "vignette250") {

        let media$: Observable<any>

        // If the mediaId is a localId, we look in cache
        if (mediaId.startsWith('local:')) {
            this.debugLog("Retrieving  " + mediaId + " from cache")

            media$ = this.cache.read(mediaId).pipe(
                switchMap(
                    (cacheValue) => {
                        if (cacheValue) {
                            const strCacheValue = cacheValue as unknown as string;
                            // If the cache value is a data url, we return it, the mediaId is not ready yet
                            if (strCacheValue.startsWith("data:")) {
                                return of({ dataUrl: strCacheValue, source: "cache" })
                            }
                            // If the cache value is not a data url, it is the new mediaId received from backend 
                            else {
                                // Fetching data from cache with the mediaId as key
                                return this.cache.read(strCacheValue).pipe(
                                    map(
                                        (secondCacheValue) => {
                                            // Deleting temporary cache entry
                                            this.cache.remove(mediaId)
                                            const strSecondCacheValue = secondCacheValue as unknown as string;
                                            // returning the data url and 'newMediaId' which can be used to update media identifiers 
                                            // in condition-report data (as in annotations for example)
                                            return { dataUrl: strSecondCacheValue, newMediaId: strCacheValue, source: "cache" }
                                        }
                                    )
                                )
                            }
                        } else {
                            // No data in cache
                            return of({});
                        }
                    }
                ),
            )
        } else {
            // If the mediaId is not local (in cache), retrieving media from network
            this.debugLog("Retrieving " + mediaId + " from network")
            media$ = this.proxy.fetch("/downloadMedias",
                {
                    "mediaId": mediaId,
                    // tslint:disable-next-line:object-literal-sort-keys
                    "fileType": fileType,
                },
                'GET', undefined, undefined, true)
                .pipe(
                    map(media => {
                        // If a media is retrieved from network, we delete the cache entry and return the dataUrl
                        if (media) {
                            this.cache.read(mediaId).pipe(first()).subscribe(cacheEntry => {
                                if (!!cacheEntry) {
                                    this.cache.remove(mediaId);
                                }
                            })
                            return { dataUrl: media, source: "network" }
                        }
                    }),
                    catchError(errors => {
                        // In case of error (typically thumbnail not ready yet), fallback on cached media if exists
                        this.debugLog("Media with id '" + mediaId + "' not available from network, falling back on cache")
                        return this.cache.read(mediaId).pipe(
                            first(),
                            map(entry => {
                                return { dataUrl: entry, source: "cache" }
                            })
                        )
                    },
                    ),
                );
        }
        return media$;
    }

    public uploadMedia(mimeType: string, file: File, localUrl: string, isAssociatedDocument?): string {

        // Setting image file in request body
        const formData = new FormData();
        formData.append("file", file)

        // We set immediately the image in cache with a generated temp id
        const tempKey = `local:${this.generateImageHash()}`
        this.setImageInCache(tempKey, localUrl)

        // Uploading image to backend
        fetch.api(`/uploadMedias/multi?mimeType=${mimeType}&isPrincipal=true&isAssociatedDocument=${!!isAssociatedDocument}`,
            {
                body: formData,
                method: 'PUT',
            }).subscribe(
                (response: any) => {
                    // When media is ready replace temp cache key by media notice id
                    this.debugLog("Set image in cache with key : " + response.mediaId)
                    this.updateCacheTempKey(tempKey, response.mediaId)
                })
        return tempKey;
    }

    public uploadPDFDocument(file: File, localUrl: string) {
        return this.uploadMediaWithoutTempUrl("application/pdf", file, undefined, true);
    }

    /**
     * Contrary to the uploadMedia method, this one doesn't generate a local id
     * in cache before uploading the media, so we have to wait a response from 
     * the backend with the media id.
     * @param mimeType 
     * @param file 
     * @param localUrl 
     */
    public async  uploadMediaWithoutTempUrl(mimeType: string, file: File, localUrl, isAssociatedDocument?) {
        // Setting image file in request body
        const formData = new FormData();
        formData.append("file", file)


        // Uploading image to backend
        return await fetch.api(`/uploadMedias/multi?mimeType=${mimeType}&isPrincipal=true&isAssociatedDocument=${!!isAssociatedDocument}`,
            {
                body: formData,
                method: 'PUT',
            })
            .pipe(first())
            .pipe(tap(
                (media) => {
                    if (localUrl) {
                        this.setImageInCache(media.mediaId, localUrl)
                    }
                }
            ))
            .toPromise()
    }

    private debugLog(message: any) {
        // tslint:disable-next-line:no-console
        console.debug("[Media-cache-management] " + JSON.stringify(message))
    }

    private setImageInCache(mediaId, localUrl) {
        this.cache.write(mediaId, localUrl)
    }

    /**
     * Changes the value of a key with another key,so that the next attempt
     * to access the first key data leads to use the new key
     * @param keyToReplace 
     * @param newKey 
     */
    private async updateCacheTempKey(keyToReplace, newKey) {
        this.cache.read(keyToReplace).pipe(first()).subscribe(
            cacheEntry => {
                if (!!cacheEntry) {
                    this.cache.write(keyToReplace, newKey);
                    this.cache.write(newKey, cacheEntry);
                    this.debugLog("Cache entry '" + keyToReplace + "' has been replaced with '" + newKey + "'")
                }
            }
        );
    }

    /**
     * Generates a random id of fixed length 
     * @param value 
     */
    private generateImageHash() {
        return '_' + Math.random().toString(36).substr(2, 9) + '-' + Math.random().toString(36).substr(2, 9)
    }
}